import { useForm, SubmitHandler } from "react-hook-form";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSnackbarContext } from "components/snackbar";
import { Form } from "components/_ui/react-hook-form/Form";
import { InputText } from "components/_ui/react-hook-form/InputText";
import { Button } from "components/buttonV2";
import { OutlineButton } from "components/button";
import GroupedButton from "components/button/grouped";
import { IAppointmentType, IAppointmentTypeDefault } from "../types/IAppointmentType";
import { useAppointmentUpdateMutation } from "../hooks/useAppointmentUpdateMutation";
import { useAppointmentAddMutation } from "../hooks/useAppointmentAddMutation";
import { useModalContext } from "components/modal";
import { useMutation, useQueryClient } from "react-query";
import Tab from "components/tab";
import { CirclePicker } from "react-color";
import { getContrastTextColorHEX } from "utils/getContrastTextColor";
import {
  blockColors,
  circleColors,
  compactColors,
  ghColors,
  sketchColors,
  twitterColors,
} from "styles/theme/colors";
import { useTranslation } from "react-i18next";
import { TextArea } from "components/input";
import DurationInput from "components/input/duration";
import AssigneeSelect from "components/react-select/assignees";
import LineItemsContainer from "containers/lineItems";
import { parseDuration, durationStrToMinutes } from "utils/duration";
import { formatInTimeZone } from "date-fns-tz";
import { format } from "date-fns";
import { convertToUTC } from "utils/utcConversion";
import { useAuthContext } from "contexts/auth";
import Switch from "rc-switch";
import Checklists from "./Checklists"

export type DefaultRowProps = {
  defaultRow: true;
  defaultItem: IAppointmentTypeDefault;
  parentColor?: string;
  archived?: boolean;
};

const formatDateInput = (d: string | null | undefined) => {
  if (d == null || d === "") return "";
  return String(d).split("T")[0];
};

const utcToTimeInput = (utc: string | null | undefined, timeZone: string) => {
  if (!utc) return "";
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  try {
    return formatInTimeZone(new Date(utc), tz, "HH:mm");
  } catch {
    return "";
  }
};

const COLOR_MAP: Record<string, string[]> = {
  block: blockColors,
  circle: circleColors,
  compact: compactColors,
  gh: ghColors,
  sketch: sketchColors,
  twitter: twitterColors,
};

interface FormData {
  label: string;
  description: string;
  color: string;
  duration_str: string;
  assigned_to: number[] | null;
  completion_note: string;
  category: string;
  date: string;
  send_reminder: boolean;
  email_confirmation: boolean;
  arrival_start_time: string;
  arrival_end_time: string;
  id: number;
  fk_company_id: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

type AssigneeOption = {
  value: number | string;
  label: string;
};

const toAssigneeIds = (value: AssigneeOption[] | AssigneeOption | null | undefined): number[] | null => {
  if (Array.isArray(value)) {
    const ids = value.map((v) => Number(v.value)).filter((n) => !isNaN(n) && n > 0);
    return ids.length ? ids : null;
  }

  if (value?.value != null) {
    const id = Number(value.value);
    return !isNaN(id) && id > 0 ? [id] : null;
  }

  return null;
};

const getAssigneeOptions = async () => {
  const employee = await axios.get(
    `${process.env.REACT_APP_SERVER_URL}/api/v1/company/employee/assignableToAppointments`
  );

  return employee.data.map(
    ({ id, first_name, last_name }: { id: number; first_name: string; last_name: string }) => ({
      value: id,
      label: `${first_name} ${last_name}`,
    })
  );
};

interface IEditAppointmentType {
  type: "parent" | "child";
  mode: "new" | "edit";
  appointmentType?: IAppointmentType;
  defaultItem?: IAppointmentTypeDefault;
  appointmentTypeId?: number;
  parentColor?: string;
}

const EditAppointmentType = (props: IEditAppointmentType) => {
  const isChild = props.type === "child";
  const isParent = props.type === "parent";
  const isNew = props.mode === "new";
  const isEdit = props.mode === "edit";

  const existing = isParent && isEdit ? props.appointmentType : undefined;
  const defaultItem = isChild && isEdit ? props.defaultItem : undefined;
  const appointmentTypeId = isChild ? props.appointmentTypeId : 0;
  const parentColor = isChild ? props.parentColor : undefined;

  const { showSnackbar } = useSnackbarContext();
  const { setModal } = useModalContext();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { timezone } = useAuthContext();
  const effectiveTz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const tBase = isChild
    ? "views.admin.appointment-types.components.EditAppointmentTypeDefault"
    : "views.admin.appointment-types.components.EditAppointmentType";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { mutate: updateParent, isLoading: updateLoading, isSuccess: updateSuccess } =
    useAppointmentUpdateMutation();
  const { mutateAsync: addParent, isLoading: addLoading, isSuccess: addSuccess } =
    useAppointmentAddMutation();

  const createDefaultMutation = useMutation(
    async (data: Record<string, unknown>) =>
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/appointment-types/${appointmentTypeId}/defaults`,
        data
      )
  );
  const updateDefaultMutation = useMutation(
    async (data: Record<string, unknown>) =>
      await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/appointment-types/defaults/${defaultItem?.id}`,
        data
      )
  );

  const [selectedColors, setSelectedColors] = useState(blockColors);
  const [activeTab, setActiveTab] = useState<"settings" | "lineitems" | "checklists">("settings");
  const [selectedAssignees, setSelectedAssignees] = useState<AssigneeOption[]>([]);

  const initDuration = isChild
    ? (defaultItem?.duration ? parseDuration(defaultItem.duration).fullStr : "")
    : (existing?.duration ? parseDuration(existing.duration).fullStr : "");

  const timeSource =
    isChild && isEdit ? defaultItem : isChild && isNew ? undefined : existing;

  const form = useForm<FormData>({
    defaultValues: {
      label: (isChild ? defaultItem?.label : existing?.label) || "",
      description: (isChild ? defaultItem?.description : existing?.description) || "",
      color: (isChild ? (defaultItem?.color || parentColor) : existing?.color) || "",
      duration_str: initDuration,
      assigned_to: (isChild ? defaultItem?.assigned_to : existing?.assigned_to) ?? null,
      completion_note: (isChild ? defaultItem?.completion_note : existing?.completion_note) || "",
      category: (isChild ? defaultItem?.category : existing?.category) || "job",
      date: formatDateInput(isChild ? defaultItem?.date : existing?.date),
      send_reminder: (isChild ? defaultItem?.send_reminder : existing?.send_reminder) ?? false,
      email_confirmation: (isChild ? defaultItem?.email_confirmation : existing?.email_confirmation) ?? false,
      arrival_start_time: utcToTimeInput(timeSource?.start_time_utc, effectiveTz),
      arrival_end_time: utcToTimeInput(timeSource?.end_time_utc, effectiveTz),
      id: existing?.id ?? 0,
      fk_company_id: 0,
      active: true,
      created_at: new Date().toLocaleDateString(),
      updated_at: new Date().toLocaleDateString(),
    },
  });

  const { setValue, watch, formState, handleSubmit } = form;
  const colorValue = watch("color");
  const labelText = watch("label");
  const assignedToValue = watch("assigned_to");
  const isDirty = formState.isDirty;

  const handleTabClick = (key: string) => {
    setSelectedColors(COLOR_MAP[key] || blockColors);
  };

  const handleChangeColor = (newColor: any) => {
    setValue("color", newColor.hex, { shouldDirty: true });
  };

  const tabData = {
    block: { tab: tr("Block") },
    circle: { tab: tr("Circle") },
    compact: { tab: tr("Compact") },
    gh: { tab: tr("GH") },
    sketch: { tab: tr("Sketch") },
    twitter: { tab: tr("Twitter") },
  };

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    const durationMinutes = durationStrToMinutes(data.duration_str);
    const refDate = data.date || format(new Date(), "yyyy-MM-dd");
    const startUtc = data.arrival_start_time
      ? convertToUTC(refDate, data.arrival_start_time, effectiveTz)
      : null;
    const endUtc = data.arrival_end_time
      ? convertToUTC(refDate, data.arrival_end_time, effectiveTz)
      : null;

    if (isParent) {
      const payload: Record<string, unknown> = {
        label: data.label,
        description: data.description || null,
        color: data.color || null,
        duration: durationMinutes,
        assigned_to: data.assigned_to,
        completion_note: data.completion_note || null,
        category: data.category || null,
        date: data.date || null,
        send_reminder: data.send_reminder,
        email_confirmation: data.email_confirmation,
        start_time_utc: startUtc,
        end_time_utc: endUtc,
      };
      if (!isNew) {
        payload.id = data.id;
      }

      if (isNew) {
        await addParent(payload as unknown as IAppointmentType);
      } else {
        updateParent(payload as unknown as IAppointmentType);
      }
      return;
    }

    const payload = {
      label: data.label || null,
      description: data.description || null,
      color: data.color || null,
      duration: durationMinutes,
      assigned_to: data.assigned_to && data.assigned_to.length ? data.assigned_to : null,
      completion_note: data.completion_note || null,
      category: data.category || null,
      date: data.date || null,
      send_reminder: data.send_reminder ?? null,
      email_confirmation: data.email_confirmation ?? null,
      start_time_utc: startUtc,
      end_time_utc: endUtc,
    };

    try {
      if (isEdit && defaultItem) {
        await updateDefaultMutation.mutateAsync(payload);
      } else {
        await createDefaultMutation.mutateAsync(payload);
      }
      queryClient.invalidateQueries();
      showSnackbar(
        isEdit
          ? tr("Default updated successfully.")
          : tr("Default created successfully.")
      );
      setModal();
    } catch {
      showSnackbar(
        isEdit
          ? tr("Failed to update default.")
          : tr("Failed to create default.")
      );
    }
  };

  const parentSuccess = isNew ? addSuccess : updateSuccess;
  useEffect(() => {
    if (!isParent) return;
    if (parentSuccess) {
      showSnackbar(
        isNew
          ? tr("Appointment type created successfully.")
          : tr("Appointment type updated successfully.")
      );
      setModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentSuccess]);

  useEffect(() => {
    let mounted = true;

    const loadAssignees = async () => {
      try {
        const options = await getAssigneeOptions();
        if (!mounted) return;

        const assignedToIds = assignedToValue;
        if (!assignedToIds || (Array.isArray(assignedToIds) && assignedToIds.length === 0)) {
          setSelectedAssignees([]);
          return;
        }

        const ids = Array.isArray(assignedToIds) ? assignedToIds : [Number(assignedToIds)];
        const matched = ids
          .map((id) => options.find((opt) => Number(opt.value) === Number(id)))
          .filter((opt): opt is AssigneeOption => opt != null);
        setSelectedAssignees(matched);
      } catch {
        if (mounted) {
          setSelectedAssignees([]);
        }
      }
    };

    loadAssignees();

    return () => {
      mounted = false;
    };
  }, [assignedToValue]);

  const loading = isParent
    ? (isNew ? addLoading : updateLoading)
    : (createDefaultMutation.isLoading || updateDefaultMutation.isLoading);
  const hasChanges = !isEdit || isDirty;

  const previewBg = colorValue || (isParent ? existing?.color : undefined) || "#f1f5f9";
  const previewFg = (colorValue || (isParent ? existing?.color : undefined))
    ? getContrastTextColorHEX(colorValue || existing?.color || "")
    : "#64748b";
  const previewLabel = labelText || tr(isChild ? "Default Name" : "Example Appointment Type");

  const canShowLineItemsTab = (isParent && isEdit && !!existing) || (isChild && isEdit && !!defaultItem);
  const canShowChecklistsTab = (isParent && !!existing?.id) || (isChild && !!defaultItem?.id);

  return (
    <div className="w-[100vw] max-w-[1000px] relative">
      <Form form={form} onSubmit={onSubmit}>
        <div className="max-h-[70vh] overflow-y-auto pr-1 pb-4">

          {/* Settings / Line Items tab toggle */}
          {(canShowLineItemsTab || canShowChecklistsTab) && (
            <div className="mb-4">
              <GroupedButton>
                <OutlineButton
                  onClick={() => setActiveTab("settings")}
                  $selected={activeTab === "settings"}
                >
                  {tr("Settings")}
                </OutlineButton>
                <OutlineButton
                  onClick={() => setActiveTab("lineitems")}
                  $selected={activeTab === "lineitems"}
                  disabled={!canShowLineItemsTab}
                >
                  {tr("Line Items")}
                </OutlineButton>
                {canShowChecklistsTab && (
                  <OutlineButton
                    onClick={() => setActiveTab("checklists")}
                    $selected={activeTab === "checklists"}
                  >
                    Checklists
                  </OutlineButton>
                )}
              </GroupedButton>
            </div>
          )}

          {/* Line Items tab */}
          {activeTab === "lineitems" && canShowLineItemsTab && (
            <div>
              {isParent && isEdit && existing && (
                <LineItemsContainer
                  id={String(existing.id)}
                  type="appointment-type"
                  noTax={true}
                />
              )}
              {isChild && isEdit && defaultItem && (
                <LineItemsContainer
                  id={String(defaultItem.id)}
                  parentId={String(appointmentTypeId)}
                  type="appointment-type-default"
                  noTax={true}
                  view="edit"
                />
              )}
            </div>
          )}

          {/* Settings tab (hidden when line items or checklists tab is active) */}
          {activeTab === "settings" && (
            <div >
              {/* Label + Preview + Toggles */}
              <div className="flex items-start justify-between space-x-4">
                <div className="w-1/2">
                  <InputText name="label" label={tr("Label")} form={form} required={isParent} />
                </div>
                <div className="w-1/4">
                  <div className="ml-[2px] text-slate-600 text-sm">{tr("Preview")}</div>
                  <div
                    className="inline-block p-2 text-sm rounded"
                    style={{ background: previewBg, color: previewFg }}
                  >
                    {previewLabel}
                  </div>
                </div>
                <div className="min-w-[140px] flex flex-col items-center">
                  <div className="mb-1">{tr("Send Reminder")}</div>
                  <Switch
                    checked={!!watch("send_reminder")}
                    onChange={(checked: boolean) =>
                      setValue("send_reminder", checked ?? false, { shouldDirty: true })
                    }
                  />
                </div>
                <div className="min-w-[140px] flex flex-col items-center">
                  <div className="mb-1">{tr("Email Confirmation")}</div>
                  <Switch
                    checked={!!watch("email_confirmation")}
                    onChange={(checked: boolean) =>
                      setValue("email_confirmation", checked ?? false, { shouldDirty: true })
                    }
                  />
                </div>
              </div>

              {/* Color picker */}
              <div className="">
                <label className="block text-sm text-slate-600 mb-2">{tr("Color")}</label>
                <Tab data={tabData} onClick={handleTabClick} defaultTab="block" />
                <CirclePicker
                  color={colorValue}
                  colors={selectedColors}
                  onChangeComplete={handleChangeColor}
                  className="mt-3 min-h-[100px]"
                  width="100%"
                />
              </div>

              {/* Category + Date */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm text-slate-600 mb-1">{tr("Category")}</label>
                  <GroupedButton>
                    <OutlineButton
                      onClick={() => setValue("category", "estimate", { shouldDirty: true })}
                      $selected={watch("category") === "estimate"}
                    >
                      {tr("Estimate")}
                    </OutlineButton>
                    <OutlineButton
                      onClick={() => setValue("category", "job", { shouldDirty: true })}
                      $selected={watch("category") === "job"}
                    >
                      {tr("Job")}
                    </OutlineButton>
                  </GroupedButton>
                </div>
                <div>
                  <label className="block text-sm text-slate-600 mb-1">{tr("Date")}</label>
                  <input
                    type="date"
                    className="w-full p-2 border-2 rounded-md border-slate-200"
                    value={watch("date") || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setValue("date", e.target.value, { shouldDirty: true })
                    }
                  />
                </div>
              </div>

              {/* Arrival Time Window */}
              <div className="mb-4">
                <label className="block text-sm text-slate-600 mb-1">{tr("Arrival Time Window")}</label>
                <p className="text-xs text-slate-500 mb-2">{tr("Optional. Used with the date above (or today) to store UTC times.")}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="time"
                    className="flex-1 min-w-[120px] p-2 border-2 rounded-md border-slate-200"
                    value={watch("arrival_start_time") || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setValue("arrival_start_time", e.target.value, { shouldDirty: true })
                    }
                  />
                  <span className="text-slate-500">{"-"}</span>
                  <input
                    type="time"
                    className="flex-1 min-w-[120px] p-2 border-2 rounded-md border-slate-200"
                    value={watch("arrival_end_time") || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setValue("arrival_end_time", e.target.value, { shouldDirty: true })
                    }
                  />
                </div>
              </div>

              {/* Duration + Assignee */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm text-slate-600 mb-1">
                    {tr(isParent ? "Default Duration" : "Duration")}
                  </label>
                  <DurationInput
                    value={watch("duration_str") || ""}
                    onValueChange={(digits) =>
                      setValue("duration_str", digits, { shouldDirty: true })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-600 mb-1">
                    {tr(isParent ? "Default Assignee" : "Assigned To")}
                  </label>
                  <AssigneeSelect
                    assigneeType="appointment"
                    value={selectedAssignees}
                    onChange={(val: AssigneeOption[] | AssigneeOption | null) => {
                      const options = Array.isArray(val) ? val : val ? [val] : [];
                      setSelectedAssignees(options);
                      setValue("assigned_to", toAssigneeIds(val), { shouldDirty: true });
                    }}
                    isClearable
                  />
                </div>
              </div>


              {/* Description */}
              <div className="mb-4">
                <label className="block text-sm text-slate-600 mb-1">{tr("Description")}</label>
                <TextArea
                  value={watch("description") || ""}
                  onChange={(e: React.ChangeEvent<any>) =>
                    setValue("description", e.target.value, { shouldDirty: true })
                  }
                  rows={2}
                  className="w-full"
                  placeholder={isParent ? tr("Default description for appointments...") : undefined}
                />
              </div>

              {/* Completion Note */}
              <div className="mb-4">
                <label className="block text-sm text-slate-600 mb-1">
                  {tr(isParent ? "Default Completion Note" : "Completion Note")}
                </label>
                <TextArea
                  value={watch("completion_note") || ""}
                  onChange={(e: React.ChangeEvent<any>) =>
                    setValue("completion_note", e.target.value, { shouldDirty: true })
                  }
                  rows={2}
                  className="w-full"
                  placeholder={tr("Pre-filled completion note...")}
                />
              </div>

            </div>
          )}

          {/* Checklists tab */}
          {activeTab === "checklists" && canShowChecklistsTab && (
            <Checklists
              appointmentTypeId={isParent ? Number(existing?.id) : Number(appointmentTypeId)}
              subAppointmentTypeId={isChild ? Number(defaultItem?.id) : null}
            />
          )}
        </div>

        {/* Unified save button at bottom for both parent and child */}
        {activeTab === "settings" && (
          <div className="mt-4 pt-3 border-t border-slate-200">
            {isParent && isNew && (
              <p className="text-xs text-slate-500 mb-3">
                {tr("After creating this type, use Edit to add default line items and checklists.")}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button type="secondary" disabled={loading} onClick={() => setModal()}>
                {tr("Cancel")}
              </Button>
              <Button
                type="primary"
                active={hasChanges}
                disabled={loading || (isEdit && !hasChanges)}
                onClick={() => handleSubmit(onSubmit)()}
              >
                {tr("Save")}
              </Button>
            </div>
          </div>
        )}
      </Form>
    </div>
  );
};


export { EditAppointmentType };
